import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/api";

export default function StripePayReturn({ kind }) {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const sessionId = params.get("session_id") || "";
  const hasSignupCache = (() => { try { return !!sessionStorage.getItem("vendiz_signup_form"); } catch { return false; } })();
  // Estado da reconciliação — chama endpoint público que consulta Stripe direto
  // e ativa o plano IMEDIATAMENTE (não depende do webhook). Idempotente.
  const [status, setStatus] = useState("loading"); // "loading" | "confirmed" | "pending" | "error"
  const [errMsg, setErrMsg] = useState("");

  useEffect(() => {
    if (kind !== "success" || !sessionId) { setStatus("confirmed"); return; }
    let cancelled = false;
    const tryReconcile = async (attempt = 1) => {
      try {
        const r = await api.post("/public/stripe/reconcile-session", { session_id: sessionId });
        if (cancelled) return;
        if (r.data?.reconciled) {
          setStatus("confirmed");
        } else if (attempt < 4) {
          // PIX ainda pending? Tenta de novo em 2s (até 4 tentativas ~8s).
          setTimeout(() => tryReconcile(attempt + 1), 2000);
        } else {
          setStatus("pending");
        }
      } catch (e) {
        if (cancelled) return;
        setErrMsg(e?.response?.data?.detail || "Não conseguimos confirmar automaticamente.");
        setStatus("error");
      }
    };
    tryReconcile();
    return () => { cancelled = true; };
  }, [kind, sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-neutral-50 to-white dark:from-[#0A0F19] dark:to-[#050810]">
      <Card className="p-8 max-w-md w-full text-center shadow-xl" data-testid={`stripe-return-${kind}`}>
        {kind === "success" ? (
          <>
            {status === "loading" && (
              <>
                <Loader2 size={56} className="mx-auto text-emerald-600 mb-3 animate-spin"/>
                <h1 className="font-display text-2xl">Confirmando pagamento…</h1>
                <p className="text-sm text-neutral-500 mt-2">Aguarde alguns segundos enquanto validamos com o Stripe.</p>
              </>
            )}
            {status === "confirmed" && (
              <>
                <CheckCircle2 size={56} className="mx-auto text-emerald-600 mb-3"/>
                <h1 className="font-display text-2xl">Pagamento confirmado!</h1>
                <p className="text-sm text-neutral-500 mt-2">Seu plano foi ativado. Faça login para começar a usar o Gestão Expert.</p>
              </>
            )}
            {status === "pending" && (
              <>
                <CheckCircle2 size={56} className="mx-auto text-amber-500 mb-3"/>
                <h1 className="font-display text-2xl">Pagamento recebido</h1>
                <p className="text-sm text-neutral-500 mt-2">Estamos aguardando a confirmação final do Stripe (pode levar alguns minutos, especialmente para PIX). Assim que confirmado, seu plano será ativado automaticamente.</p>
              </>
            )}
            {status === "error" && (
              <>
                <CheckCircle2 size={56} className="mx-auto text-emerald-600 mb-3"/>
                <h1 className="font-display text-2xl">Pagamento em processamento</h1>
                <p className="text-sm text-neutral-500 mt-2">Recebemos seu pagamento. Se o plano não aparecer ativo em poucos minutos, entre em contato com o suporte pelo WhatsApp.</p>
                {errMsg && <p className="text-[10px] text-neutral-400 mt-2">{errMsg}</p>}
              </>
            )}
            {sessionId && <p className="text-[10px] font-mono text-neutral-400 mt-3 break-all">{sessionId}</p>}
          </>
        ) : (
          <>
            <XCircle size={56} className="mx-auto text-neutral-400 mb-3"/>
            <h1 className="font-display text-2xl">Pagamento cancelado</h1>
            <p className="text-sm text-neutral-500 mt-2">Você pode tentar novamente quando quiser.</p>
          </>
        )}
        <Button onClick={()=>nav("/login")} className="w-full mt-5 bg-[#0A0A0A] text-white" data-testid="return-to-login-btn">Voltar ao login</Button>
        {kind === "cancel" && hasSignupCache && (
          <Button onClick={()=>nav("/cadastro")} variant="outline" className="w-full mt-2" data-testid="return-to-signup-btn">
            Continuar cadastro
          </Button>
        )}
      </Card>
    </div>
  );
}
